import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import ListingCard from '@/components/ListingCard';
import MapView from '@/components/MapView';
import SaveSearchButton from '@/components/SaveSearchButton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Search, SlidersHorizontal, Map as MapIcon, List, X } from 'lucide-react';
import Mascot from '@/components/Mascot';

const CROPS = ['Vite','Olivo','Grano','Mais','Ortaggi','Frutteto','Foraggio','Nocciolo'];
const FEATURE_KEYS = [
  ['water','Acqua'], ['irrigation','Irrigazione'], ['electricity','Elettricità'],
  ['road_access','Accesso stradale'], ['buildings','Fabbricati'], ['fenced','Recinzioni'],
];

const initial = {
  q: '', listing_type: 'all', region: '', price_min: '', price_max: '',
  area_min: '', area_max: '', crop: 'all', sort: 'recent',
  features: {}, verified_owner: false,
};

export default function Explore() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(initial);
  const [showFilters, setShowFilters] = useState(false);
  const [mobileView, setMobileView] = useState('list'); // list | map

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.q) params.q = f.q;
      if (f.listing_type !== 'all') params.listing_type = f.listing_type;
      if (f.region) params.region = f.region;
      if (f.price_min) params.price_min = f.price_min;
      if (f.price_max) params.price_max = f.price_max;
      if (f.area_min) params.area_min = f.area_min;
      if (f.area_max) params.area_max = f.area_max;
      if (f.crop !== 'all') params.crop = f.crop;
      if (f.features.water) params.water = true;
      if (f.verified_owner) params.verified_owner = true;
      params.sort = f.sort;
      params.limit = 60;
      const { data } = await api.get('/listings', { params });
      let list = data.items || [];
      // client-side filter for additional feature flags (backend supports 'water' via query)
      const activeFeats = Object.keys(f.features).filter(k => f.features[k] && k !== 'water');
      if (activeFeats.length) {
        list = list.filter(l => activeFeats.every(k => l.features?.[k]));
      }
      setItems(list); setTotal(data.total || list.length);
    } catch {}
    setLoading(false);
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const toggleFeat = (k) => setF({ ...f, features: { ...f.features, [k]: !f.features[k] } });
  const reset = () => setF(initial);

  const Filters = (
    <div className="space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Cerca</Label>
        <div className="relative mt-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={f.q} onChange={e=>setF({...f, q:e.target.value})} placeholder="titolo, descrizione..." className="pl-9"/>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Operazione</Label>
        <Select value={f.listing_type} onValueChange={v=>setF({...f, listing_type:v})}>
          <SelectTrigger className="mt-2"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="rent">Affitto</SelectItem>
            <SelectItem value="sale">Vendita</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Regione</Label>
        <Input value={f.region} onChange={e=>setF({...f, region:e.target.value})} placeholder="Es. Toscana" className="mt-2"/>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Superficie (ha)</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input type="number" placeholder="min" value={f.area_min} onChange={e=>setF({...f, area_min:e.target.value})}/>
          <Input type="number" placeholder="max" value={f.area_max} onChange={e=>setF({...f, area_max:e.target.value})}/>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Prezzo (€)</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input type="number" placeholder="min" value={f.price_min} onChange={e=>setF({...f, price_min:e.target.value})}/>
          <Input type="number" placeholder="max" value={f.price_max} onChange={e=>setF({...f, price_max:e.target.value})}/>
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Coltura</Label>
        <Select value={f.crop} onValueChange={v=>setF({...f, crop:v})}>
          <SelectTrigger className="mt-2"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            {CROPS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Caratteristiche</Label>
        <div className="mt-3 space-y-2">
          {FEATURE_KEYS.map(([k,l]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!f.features[k]} onCheckedChange={()=>toggleFeat(k)}/> {l}
            </label>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fiducia</Label>
        <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-verified-owner">
          <Checkbox checked={!!f.verified_owner} onCheckedChange={()=>setF({...f, verified_owner: !f.verified_owner})}/>
          Solo proprietari verificati
        </label>
      </div>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={reset} className="flex-1"><X size={12} className="mr-1"/> Reset</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-2">Esplora</div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter">Il tuo prossimo terreno.</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={f.sort} onValueChange={v=>setF({...f, sort:v})}>
            <SelectTrigger className="w-[180px]"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Più recenti</SelectItem>
              <SelectItem value="price_asc">Prezzo crescente</SelectItem>
              <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
              <SelectItem value="area_desc">Superficie maggiore</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile toggle */}
      <div className="lg:hidden flex gap-2 mb-4 flex-wrap">
        <Sheet open={showFilters} onOpenChange={setShowFilters}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-full"><SlidersHorizontal size={14} className="mr-2"/> Filtri</Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[320px] overflow-y-auto">
            <SheetHeader><SheetTitle>Filtri</SheetTitle></SheetHeader>
            <div className="mt-6">{Filters}</div>
          </SheetContent>
        </Sheet>
        <Button variant={mobileView==='list'?'default':'outline'} size="sm" className="rounded-full" onClick={()=>setMobileView('list')}><List size={14} className="mr-1"/> Lista</Button>
        <Button variant={mobileView==='map'?'default':'outline'} size="sm" className="rounded-full" onClick={()=>setMobileView('map')}><MapIcon size={14} className="mr-1"/> Mappa</Button>
        <SaveSearchButton filters={f} size="sm"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_460px] gap-6">
        {/* Desktop filters */}
        <aside className="hidden lg:block sticky top-24 self-start h-fit rounded-2xl border border-border p-5 bg-card max-h-[calc(100vh-7rem)] overflow-y-auto">
          {Filters}
          <div className="mt-4 pt-4 border-t border-border">
            <SaveSearchButton filters={f}/>
          </div>
        </aside>

        {/* Results */}
        <section className={`${mobileView==='map'?'hidden lg:block':''}`}>
          <div className="text-sm text-muted-foreground mb-4">
            {loading ? 'Caricamento…' : `${total} terreni trovati`}
          </div>
          {items.length === 0 && !loading ? (
            <div className="rounded-2xl border border-dashed border-border p-16 text-center">
              <div className="flex justify-center">
                <Mascot pose="search" size={150}/>
              </div>
              <h3 className="mt-6 font-display text-2xl">Giulio non trova nulla con questi filtri.</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">Prova a rimuovere alcuni filtri o ad ampliare la zona di ricerca.</p>
              <Button className="mt-6 rounded-full" variant="outline" onClick={reset}>Rimuovi filtri</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {items.map(l => <ListingCard key={l.listing_id} listing={l}/>)}
            </div>
          )}
        </section>

        {/* Map */}
        <aside className={`${mobileView==='list'?'hidden lg:block':''} lg:sticky lg:top-24 self-start h-[70vh] lg:h-[calc(100vh-7rem)]`}>
          <MapView listings={items} height="100%"/>
        </aside>
      </div>
    </div>
  );
}
